import { useEffect } from "react";
import { Navigate, ScrollRestoration, useRoutes, useParams, Outlet, useLocation } from "react-router-dom";
// auth
import { GuestGuard } from "../sections/auth/GuestGuard";
import { AuthGuard } from "../sections/auth/AuthGuard";
// layouts
import CompactLayout from "../layouts/compact";
import DashboardLayout from "../layouts/dashboard";
// config
import { PATH_AFTER_LOGIN } from "../config-global";
//
import {
  Page404,
  LoginPage,
  CareersPage,
  UserListPage,
  AuthCallbackPage,
  GmailCallbackPage,
  GmailDebugPage,
  GmailOAuthCallback,
  UserManagementPage,
  UserEditPage,
  UserCreatePage,
  Home,
  Profile,
  EmployeeProfile,
  CustomerProfile,
  Schedule,
  SitePage,
  ContactsPage,
  FormSubmissionPage,
  JobReqsPage,
  JobApplicationsPage,
  ShiftTemplate,
  PostOrderEditor,
  TimeOffRequests,
  FleetVehiclesPage,
  FleetDriversPage,
  FleetMaintenancePage,
  // FleetTripsPage,
  FleetFuelLogsPage,
  FleetDashboardPage,
  InsurancePoliciesPage,
  DefectReportListPage,
  DriverApprovalRequestsPage,
  CertificateRequestsPage,
  ChatPage,
  SiteTaskTemplatesPage,
  SiteTaskAssignmentsPage,
  SiteTaskDashboardPage,
  TaskCompletionTemplateListPage,
  EquipmentListPage,
  EquipmentFormPage,
  ShiftTaskDetailPage,
  IncidentReportDetailPage,
  TaskPerformanceChartPage,
  TaskCategoryDistributionPage,
  IncompleteTaskAnalysisPage,
  RoundPerformanceMetricsPage,
  GuardPerformanceViewPage,
  EquipmentStatusReportPage,
  EquipmentAnalyticsPage,
  GamificationSettingsPage,
  GamificationLeaderboardPage,
  AchievementBadgesPage,
  GamificationReportsPage,
  NotificationInboxPage,
  NotificationTesterPage,
  GraphQLTesterPage,
  PasswordChangeSuccessPage,
  // DarListPage,
  // DarEditPage,
  // DarInternalPage,
  // DarSendEmailPage,
  // EmailTemplateManagementPage,
} from "./elements";
import Settings from "../pages/Settings";
import LogoutInitPage from "../pages/auth/LogoutInitPage";
import LogoutPage from "../pages/auth/LogoutPage";

import {
  CallbackGuard,
  RoleBasedGuard,
  useAuthContext,
} from "../sections/auth";
import { roles } from "../assets/data/constant";
import WorkOrderList from "../pages/fleet/WorkOrderList";
import WorkOrderForm from "../pages/fleet/WorkOrderForm";
import InspectionList from "../pages/fleet/InspectionList";
import InspectionForm from "../pages/fleet/InspectionForm";
import RegistrationsList from "../pages/fleet/RegistrationsList";
import WarrantyRecordList from "../pages/fleet/WarrantyRecordList";
// FCM
import FCMInitializer from '../components/fcm/FCMInitializer';
// ----------------------------------------------------------------------

// Redirect component for customer-profile -> customers
function CustomerProfileRedirect() {
  const params = useParams();
  const { id, actionType, recordId } = params;
  
  if (id && actionType) {
    const path = `/customers/${id}/${actionType}${recordId ? `/${recordId}` : ""}`;
    return <Navigate to={path} replace />;
  }
  
  if (id) {
    return <Navigate to={`/customers/${id}`} replace />;
  }
  
  return <Navigate to="/customers" replace />;
}

const AUTH_RETURN_PATH_KEY = "auth0_return_path";

export default function Router() {
  const { user } = useAuthContext();
  const location = useLocation();

  // Persist current path so we can restore it after Auth0 redirect (e.g. silent refresh on reload)
  useEffect(() => {
    const pathname = location.pathname;
    if (pathname && !pathname.startsWith("/auth")) {
      sessionStorage.setItem(AUTH_RETURN_PATH_KEY, pathname);
    }
  }, [location.pathname]);

  const routes = useRoutes([
    {
      path: "auth",
      element: <Outlet />,
      children: [
        {
          index: true,
          element: <Navigate to="/auth/login" replace />,
        },
        {
          path: "login",
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: "callback",
          element: <AuthCallbackPage />,
        },
        {
          path: "logout-init",
          element: <LogoutInitPage />,
        },
        {
          path: "password-change-success",
          element: (
            <GuestGuard>
              <PasswordChangeSuccessPage />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: "logout",
      element: <LogoutPage />,
    },
    // Note: Gmail OAuth uses the same /auth/callback path as Auth0
    {
      path: "gmail-debug",
      element: (
        <AuthGuard>
          <GmailDebugPage />
        </AuthGuard>
      ),
    },
    {
      path: "careers/:id?/:apply?",
      element: (
        <GuestGuard>
          {/* <ScrollRestoration> */}
          <CareersPage />
          {/* </ScrollRestoration> */}
        </GuestGuard>
      ),
    },
    {
      path: "/dashboard",
      element: (
        <AuthGuard>
          {/* <ScrollRestoration> */}
          <DashboardLayout />
          {/* </ScrollRestoration> */}
        </AuthGuard>
      ),
      children: [
        // { element: <Navigate to={PATH_AFTER_LOGIN} /> },
        {
          path: "user-management",
          element: <UserManagementPage />,
          children: [
            {
              element: (
                <Navigate to="/dashboard/user-management/list" replace />
              ),
              index: true,
            },
            {
              path: "list",
              element: (
                <RoleBasedGuard roles={["Administrator"]} hasContent>
                  <UserListPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: "new",
              element: (
                <RoleBasedGuard roles={["Administrator"]} hasContent>
                  <UserCreatePage />
                </RoleBasedGuard>
              ),
            },
            { path: ":id", element: <UserEditPage /> },
          ],
        },
      ],
    },
    {
      path: "/",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to={PATH_AFTER_LOGIN} replace />,
        },
        {
          path: "home",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <Home />
            </RoleBasedGuard>
          ),
        },
        {
          path: "my-profile/:tabId?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <Profile />
            </RoleBasedGuard>
          ),
        },
        {
          path: "my-profile/:id/:tabId?/:newFormId?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <Profile />
            </RoleBasedGuard>
          ),
        },
        {
          path: "employees/:id?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <EmployeeProfile />
            </RoleBasedGuard>
          ),
        },
        {
          path: "employees/:id/:tabId?/:formCode?/:newFormId?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <EmployeeProfile />
            </RoleBasedGuard>
          ),
        },
        {
          path: "customers/:id?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <CustomerProfile />
            </RoleBasedGuard>
          ),
        },
        {
          path: "customers/:id/:actionType/:recordId?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <SitePage />
            </RoleBasedGuard>
          ),
        },
        // Redirect old customer-profile routes to new customers routes
        {
          path: "customer-profile/:id?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <CustomerProfileRedirect />
            </RoleBasedGuard>
          ),
        },
        {
          path: "customer-profile/:id/:actionType/:recordId?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <CustomerProfileRedirect />
            </RoleBasedGuard>
          ),
        },
        {
          path: "schedules",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <Schedule />
            </RoleBasedGuard>
          ),
        },
        {
          path: "shift-templates",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <ShiftTemplate />
            </RoleBasedGuard>
          ),
        },
        {
          path: "shift-templates/:shiftTempId?",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <ShiftTemplate />
            </RoleBasedGuard>
          ),
        },
        {
          path: "contacts",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <ContactsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "jobs-reqs/:id?",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <JobReqsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "jobs-applications/:id?",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <JobApplicationsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "forms/:formSubmissionId/:empId?/:backPage?/:tabId?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <FormSubmissionPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "forms/:newForm/:formId/:empId",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <FormSubmissionPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "post-order/editor",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <PostOrderEditor />
            </RoleBasedGuard>
          ),
        },
        {
          path: "time-off-requests/:id?",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <TimeOffRequests />
            </RoleBasedGuard>
          ),
        },
        // {
        //   path: "latest-changes",
        //   element: (
        //     <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
        //       <LatestChangesPage />
        //     </RoleBasedGuard>
        //   ),
        // },
        {
          path: "/fleet/vehicles",
          element: <FleetVehiclesPage />,
        },
        {
          path: "insurance-policies",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <InsurancePoliciesPage />
            </RoleBasedGuard>
          ),
        },
        // {
        //   path: '/fleet/driver-approval-requests',
        //   element: (
        //     <RoleBasedGuard roles={['Administrator']} hasContent>
        //       <DriverApprovalRequestsPage />
        //     </RoleBasedGuard>
        //   ),
        // },
        // {
        //   path: '/fleet/certificate-requests',
        //   element: (
        //     <RoleBasedGuard roles={['Administrator']} hasContent>
        //       <CertificateRequestsPage />
        //     </RoleBasedGuard>
        //   ),
        // },
        {
          path: "/fleet/drivers",
          element: <FleetDriversPage />,
        },
        {
          path: "/fleet/maintenance",
          element: <FleetMaintenancePage />,
        },
        // {
        //   path: '/fleet/trips',
        //   element: <FleetTripsPage />,
        // },
        {
          path: "/fleet/fuel-logs",
          element: <FleetFuelLogsPage />,
        },
        {
          path: "/fleet/dashboard",
          element: <FleetDashboardPage />,
        },
        {
          path: "/fleet/work-orders",
          element: <WorkOrderList />,
        },
        {
          path: "/fleet/work-orders/new",
          element: <WorkOrderForm />,
        },
        {
          path: "/fleet/work-orders/:id",
          element: <WorkOrderForm />,
        },
        {
          path: "/fleet/inspections",
          element: <InspectionList />,
        },
        {
          path: "/fleet/inspections/new",
          element: <InspectionForm />,
        },
        {
          path: "/fleet/inspections/:id",
          element: <InspectionForm />,
        },
        {
          path: "fleet/defect-reports",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <DefectReportListPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "/registrations",
          element: <RegistrationsList />,
        },
        {
          path: "/fleet/warranty-records",
          element: <WarrantyRecordList />,
        },
        // Site Tasks Routes 
        {
          path: "site-tasks/dashboard",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <SiteTaskDashboardPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/templates",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <SiteTaskTemplatesPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/assignments",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <SiteTaskAssignmentsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/completion-templates",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <TaskCompletionTemplateListPage />
            </RoleBasedGuard>
          ),
        },
        // Site Tasks - Equipment
        {
          path: "site-tasks/equipment",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <EquipmentListPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/equipment/new",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <EquipmentFormPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/equipment/:id/edit",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <EquipmentFormPage />
            </RoleBasedGuard>
          ),
        },
        // Site Tasks - Detail Views
        {
          path: "site-tasks/shift-tasks/:id",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <ShiftTaskDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/incidents/:id",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <IncidentReportDetailPage />
            </RoleBasedGuard>
          ),
        },
        // Site Tasks - Analytics
        {
          path: "site-tasks/analytics/performance",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <TaskPerformanceChartPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/analytics/category-distribution",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <TaskCategoryDistributionPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/analytics/incomplete-tasks",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <IncompleteTaskAnalysisPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/analytics/round-performance",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <RoundPerformanceMetricsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/analytics/guard-performance",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <GuardPerformanceViewPage />
            </RoleBasedGuard>
          ),
        },
        // Site Tasks - Equipment Reports
        {
          path: "site-tasks/equipment/status-report",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <EquipmentStatusReportPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/equipment/analytics",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <EquipmentAnalyticsPage />
            </RoleBasedGuard>
          ),
        },
        // Site Tasks - Gamification
        {
          path: "site-tasks/gamification/settings",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <GamificationSettingsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/gamification/leaderboard",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <GamificationLeaderboardPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/gamification/achievements",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <AchievementBadgesPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "site-tasks/gamification/reports",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <GamificationReportsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "my-profile/settings",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <Settings />
            </RoleBasedGuard>
          ),
        },
        {
          path: "chat",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <ChatPage />
            </RoleBasedGuard>
          ),
          // Notification Routes
        },
        {
          path: "notifications",
          element: (
            <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
              <NotificationInboxPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "notifications/tester",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <NotificationTesterPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: "notifications/graphql-test",
          element: (
            <RoleBasedGuard roles={["Administrator"]} hasContent>
              <GraphQLTesterPage />
            </RoleBasedGuard>
          ),
        },
        // DAR Routes - COMMENTED OUT TEMPORARILY
        // {
        //   path: "dar",
        //   element: (
        //     <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
        //       <DarListPage />
        //     </RoleBasedGuard>
        //   ),
        // },
        // {
        //   path: "dar/:id/edit",
        //   element: (
        //     <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
        //       <DarEditPage />
        //     </RoleBasedGuard>
        //   ),
        // },
        // {
        //   path: "dar/:id/internal",
        //   element: (
        //     <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
        //       <DarInternalPage />
        //     </RoleBasedGuard>
        //   ),
        // },
        // {
        //   path: "dar/:id/send-email",
        //   element: (
        //     <RoleBasedGuard roles={["Administrator", "Employee"]} hasContent>
        //       <DarSendEmailPage />
        //     </RoleBasedGuard>
        //   ),
        // },
        // {
        //   path: "admin/email-templates",
        //   element: (
        //     <RoleBasedGuard roles={["Administrator"]} hasContent>
        //       <EmailTemplateManagementPage />
        //     </RoleBasedGuard>
        //   ),
        // },
      ],
    },
    {
      element: <CompactLayout />,
      children: [{ path: "404", element: <Page404 /> }],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);

  return (
    <>
      <FCMInitializer />
      {routes}
    </>
  );
}
